import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../Constant/Auth';
import '../parentTables.css';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getMyFines,
  initiateFinePayment,
  getErrorMessage
} from '../../api/api';

const FINE_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'waived', label: 'Waived' },
  { value: 'cancelled', label: 'Cancelled' }
];

// Lazily inject the HDFC / Juspay checkout SDK. Only loaded when a parent
// actually clicks "Pay Now", so it never affects the rest of the app.
const loadGatewayScript = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(false);
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Payment SDK failed to load'));
    document.body.appendChild(script);
  });

const ParentFineList = () => {
  const { user } = useAuth();

  const libraryId = user?.library_id || user?.libraryId;

  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [payingId, setPayingId] = useState(null);

  const fetchFines = async () => {
    try {
      if (!user?.id) return;

      setLoading(true);

      // Self-scoped: the backend resolves "my" fines from the verified
      // login, never from a client-supplied id — so this only ever returns
      // this parent's own fines, never any other member's.
      const data = await getMyFines();
      setFines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Parent fines error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const statusOf = (fine) => String(fine.fine_status || fine.status || '').toLowerCase();
  const amountOf = (fine) => Number(fine.amount ?? fine.fine_amount ?? 0);
  const paidOf = (fine) => Number(fine.paid_amount ?? 0);
  const balanceOf = (fine) =>
    Number(fine.balance_amount ?? (amountOf(fine) - paidOf(fine)) ?? 0);

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'paid') return 'bg-success';
    if (s === 'pending') return 'bg-warning text-dark';
    if (s === 'partial') return 'bg-info text-dark';
    if (s === 'waived') return 'bg-secondary';
    if (s === 'cancelled') return 'bg-dark';
    return 'bg-secondary';
  };

  const formatMoney = (value) => `₹ ${Number(value || 0).toLocaleString('en-IN')}`;
  const formatDate = (value) => (value ? String(value).slice(0, 10) : '-');

  // Start an online HDFC payment for a single fine. The backend creates the
  // order (computing the amount itself) and returns either an SDK session or a
  // redirect URL; we handle both. Final success is confirmed on the callback page.
  const handlePayNow = async (fine) => {
    try {
      setPayingId(fine.id);

      const order = await initiateFinePayment(fine.id);
      if (!order) throw new Error('Could not start payment');

      const returnUrl =
        `${window.location.origin}/parent-payment-callback?order_id=${encodeURIComponent(order.order_id)}`;

      // CCAvenue / FSS style: backend hands us a URL to redirect the browser to.
      if (order.redirect_url) {
        window.location.href = order.redirect_url;
        return;
      }

      // SmartGateway / Juspay style: load the SDK and open hosted checkout.
      await loadGatewayScript(order.sdk_url);

      const checkout = window.HDFCCheckout || window.Juspay;
      if (!checkout || typeof checkout.open !== 'function') {
        throw new Error('Payment SDK is not available');
      }

      checkout.open({
        session: order.payment_session,
        returnUrl
      });
    } catch (error) {
      console.error('Pay now error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setPayingId(null);
    }
  };

  const filteredFines = useMemo(
    () =>
      statusFilter
        ? fines.filter((item) => statusOf(item) === statusFilter)
        : fines,
    [fines, statusFilter]
  );

  const summary = useMemo(() => {
    return fines.reduce(
      (acc, fine) => {
        acc.total += amountOf(fine);
        acc.paid += paidOf(fine);
        acc.outstanding += balanceOf(fine);
        return acc;
      },
      { total: 0, paid: 0, outstanding: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fines]);

  return (
    <div className="container-fluid mt-3 pt-page">
      <div className="pt-page-header">
        <div className="pt-page-header__left">
          <span className="pt-page-header__icon">
            <i className="fas fa-receipt" />
          </span>
          <div>
            <h3 className="pt-page-header__title">My Fines</h3>
            <div className="pt-page-header__subtitle">View your fines, amount due, reason and status</div>
          </div>
        </div>
      </div>

      <div className="pt-stats">
        <div className="pt-stat" style={{ '--c': '#4f46e5' }}>
          <span className="pt-stat-ico"><i className="bx bx-receipt" /></span>
          <div className="pt-stat-text">
            <div className="pt-stat-label">Total Fines</div>
            <div className="pt-stat-value">{formatMoney(summary.total)}</div>
          </div>
        </div>

        <div className="pt-stat" style={{ '--c': '#16a34a' }}>
          <span className="pt-stat-ico"><i className="bx bx-wallet" /></span>
          <div className="pt-stat-text">
            <div className="pt-stat-label">Total Paid</div>
            <div className="pt-stat-value">{formatMoney(summary.paid)}</div>
          </div>
        </div>

        <div className="pt-stat" style={{ '--c': '#dc2626' }}>
          <span className="pt-stat-ico"><i className="bx bx-error-circle" /></span>
          <div className="pt-stat-text">
            <div className="pt-stat-label">Outstanding</div>
            <div className="pt-stat-value">{formatMoney(summary.outstanding)}</div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="pt-filter-row">
            <SearchableSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={FINE_STATUS_OPTIONS}
              placeholder="All Status"
            />

            <button
              className="btn btn-outline-secondary"
              onClick={() => setStatusFilter('')}
            >
              Reset
            </button>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Type</th>
                    <th scope="col">Reason</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Paid</th>
                    <th scope="col">Balance</th>
                    <th scope="col">Due Date</th>
                    <th scope="col">Status</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredFines.length > 0 ? (
                    filteredFines.map((fine, index) => (
                      <tr key={fine.id || index}>
                        <td>{index + 1}</td>
                        <td className="text-capitalize">{fine.fine_type || '-'}</td>
                        <td>{fine.reason || fine.remarks || '-'}</td>
                        <td>{formatMoney(amountOf(fine))}</td>
                        <td>{formatMoney(paidOf(fine))}</td>
                        <td>{formatMoney(balanceOf(fine))}</td>
                        <td>{formatDate(fine.due_date || fine.created_at)}</td>
                        <td>
                          <span
                            className={`badge ${getStatusBadge(statusOf(fine))}`}
                          >
                            {fine.fine_status || fine.status || '-'}
                          </span>
                        </td>
                        <td>
                          {balanceOf(fine) > 0 ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => handlePayNow(fine)}
                              disabled={payingId === fine.id}
                            >
                              {payingId === fine.id ? 'Starting...' : 'Pay Now'}
                            </button>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        No fines found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentFineList;
